import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { PrimeNgModule } from '../../../core/shared/primeng.module';

export interface GroupedBarDataset {
  label: string;
  data: number[];
  color?: string;
}

export interface GroupedBarChartConfig {
  /** X-axis labels. Pass a string array per item for multi-line labels, e.g. [['MON', '(16 APR)'], ...] */
  labels: (string | string[])[];
  datasets: GroupedBarDataset[];
  /** Optional prefix prepended to every tooltip/axis value, e.g. '$' */
  currencyPrefix?: string;
  yAxisTitle?: string;
  xAxisTitle?: string;
}

@Component({
  selector: 'app-grouped-bar-chart',
  standalone: true,
  imports: [PrimeNgModule, CommonModule],
  templateUrl: './grouped-bar-chart.component.html',
  styleUrl: './grouped-bar-chart.component.scss',
})
export class GroupedBarChartComponent implements OnInit, OnChanges {
  @Input() config!: GroupedBarChartConfig;
  /** Optional card title rendered above the chart */
  @Input() title?: string;
  /** Height of the chart canvas. Accepts any CSS value, e.g. '360px', '50vh'. Defaults to '450px'. */
  @Input() chartHeight: string = '450px';

  chartData: any;
  chartOptions: any;
  chartPlugins: any[] = [];

  /** Default color palette matching the design in the reference image */
  private readonly palette = [
    '#C105B3', // Design – hot pink / magenta
    '#DED6FF', // Execution – soft lavender
    '#2F76FF', // Reporting & Analytics – bright blue
    '#38369A', // Maintenance – dark navy
  ];

  ngOnInit(): void {
    this.buildChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      this.buildChart();
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  private formatTick(value: number, prefix = ''): string {
    if (value >= 1_000_000) {
      const m = value / 1_000_000;
      return `${prefix}${m % 1 === 0 ? m : m.toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `${prefix}${Math.round(value / 1_000)}k`;
    }
    return `${prefix}${value}`;
  }

  // ─── Chart build ────────────────────────────────────────────────────────────

  private buildChart(): void {
    if (!this.config) return;

    const { labels, datasets, currencyPrefix = '', yAxisTitle, xAxisTitle } = this.config;

    this.chartData = {
      labels,
      datasets: datasets.map((ds, i) => ({
        label: ds.label,
        data: ds.data,
        backgroundColor: ds.color ?? this.palette[i % this.palette.length],
        borderRadius: 5,
        borderSkipped: false,
        maxBarThickness: 14.6,
        barPercentage: 0.9,
        categoryPercentage: 0.6,
      })),
    };

    // Custom plugin: draws a light-grey highlight behind the hovered bar group
    const groupHighlightPlugin = {
      id: 'groupHighlight',
      beforeDatasetsDraw: (chart: any) => {
        const { ctx, chartArea, data } = chart;
        const activeElements = chart.getActiveElements();
        if (!activeElements || activeElements.length === 0) return;

        const dataIndex = activeElements[0].index;
        const meta0 = chart.getDatasetMeta(0);
        if (!meta0?.data?.[dataIndex]) return;

        const barCount = data.datasets.length;
        // Measure the total group width by inspecting all dataset bars at this index
        let minX = Infinity;
        let maxX = -Infinity;
        for (let d = 0; d < barCount; d++) {
          const m = chart.getDatasetMeta(d);
          if (!m?.data?.[dataIndex]) continue;
          const bar = m.data[dataIndex];
          const props = bar.getProps(['x', 'width'], true);
          minX = Math.min(minX, props.x - props.width / 2);
          maxX = Math.max(maxX, props.x + props.width / 2);
        }

        const pad = 8;
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
        const rectX = minX - pad;
        const rectW = maxX - minX + pad * 2;
        const rectY = chartArea.top;
        const rectH = chartArea.height;
        const radius = 6;
        ctx.beginPath();
        ctx.moveTo(rectX + radius, rectY);
        ctx.lineTo(rectX + rectW - radius, rectY);
        ctx.quadraticCurveTo(rectX + rectW, rectY, rectX + rectW, rectY + radius);
        ctx.lineTo(rectX + rectW, rectY + rectH - radius);
        ctx.quadraticCurveTo(rectX + rectW, rectY + rectH, rectX + rectW - radius, rectY + rectH);
        ctx.lineTo(rectX + radius, rectY + rectH);
        ctx.quadraticCurveTo(rectX, rectY + rectH, rectX, rectY + rectH - radius);
        ctx.lineTo(rectX, rectY + radius);
        ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      },
    };

    this.chartPlugins = [groupHighlightPlugin];

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'nearest',
        intersect: true,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1E1B3A',
          titleColor: '#FFFFFF',
          bodyColor: '#FFFFFF',
          titleFont: { size: 13, weight: '600' },
          bodyFont: { size: 16, weight: '700' },
          padding: { x: 14, y: 12 },
          cornerRadius: 10,
          displayColors: false,
          caretSize: 8,
          caretPadding: 6,
          callbacks: {
            title: (items: any[]) => items[0]?.dataset?.label ?? '',
            label: (context: any) => {
              const value: number = context.raw ?? 0;
              return `${currencyPrefix}${value.toLocaleString()}`;
            },
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: '#858381',
            font: { size: 11, family: 'inherit' },
            maxRotation: 0,
            padding: 6,
          },
          ...(xAxisTitle ? { title: { display: true, text: xAxisTitle, color: '#858381', font: { size: 11 } } } : {}),
        },
        y: {
          grid: {
            color: '#E8E7E6',
            lineWidth: 1,
          },
          border: { display: false, dash: [4, 4] },
          beginAtZero: true,
          ticks: {
            color: '#858381',
            font: { size: 11, family: 'inherit' },
            padding: 8,
            callback: (value: any) => this.formatTick(value, ''),
          },
          ...(yAxisTitle ? { title: { display: true, text: yAxisTitle, color: '#858381', font: { size: 11 } } } : {}),
        },
      },
    };
  }

  get legendItems(): { label: string; color: string }[] {
    if (!this.config?.datasets) return [];
    return this.config.datasets.map((ds, i) => ({
      label: ds.label,
      color: ds.color ?? this.palette[i % this.palette.length],
    }));
  }
}
